import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axios";
import EntityManagement from "../../components/formTemplates/EntityManagement";
import EntityTable from "../../components/templateComponents/EntityTable";
import useDropdownFetch from "../../components/apiFetch/useDropdownFetch";
import useDropdownPostFetch from "../../components/apiFetch/useDropdownPostFetch";
import { entitiesConfig } from "../../components/templateComponents/constants";

const Shops = () => {
  const entityConfig = entitiesConfig.shops;

  const [formData, setFormData] = useState(() => {
    const initialData = {};
    entityConfig.formFields.forEach((field) => {
      initialData[field.name] = "";
    });
    return initialData;
  });

  const [entities, setEntities] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editingEntityId, setEditingEntityId] = useState(null);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const firstFormFieldRef = useRef(null);

  // Fetch dropdown options
  const merchantOptions = useDropdownPostFetch(
    entityConfig.dropdowns.merchant,
    { view_type: "view" }
  );
  const domainOptions = useDropdownFetch(entityConfig.dropdowns.domain);

  // Fetch all shops
  const fetchEntities = async () => {
    try {
      const response = await axiosInstance.post(entityConfig.endpoints.fetch, {
        view_type: "view all",
      });
      const fetchedEntities = response.data.message || [];
      setEntities(fetchedEntities);
    } catch (error) {
      console.error("Error fetching shops:", error);
    }
  };

  useEffect(() => {
    fetchEntities();
  }, []);

  const handleUpdate = async (entityId, updatedData) => {
    try {
      const response = await axiosInstance.post(entityConfig.endpoints.update, {
        shop_id: entityId,
        ...updatedData,
      });

      if (response.data.status === "success") {
        await fetchEntities();
        resetForm();
        setIsTableModalOpen(false);
        setSuccessMessage("Shop updated successfully!");
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        console.error("Failed to update shop:", response.data);
      }
    } catch (error) {
      console.error("Error updating shop:", error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const formattedFormData = {
      ...formData,
      merchant_id: parseInt(formData.merchant_id, 10),
      domain_id: parseInt(formData.domain_id, 10),
      workdays: parseInt(formData.workdays, 10),
    };

    if (editMode) {
      await handleUpdate(editingEntityId, formattedFormData);
    } else {
      try {
        const response = await axiosInstance.post(
          entityConfig.endpoints.create,
          formattedFormData
        );

        if (response.data.status === "success") {
          await fetchEntities();
          resetForm();
          setSuccessMessage("Shop added successfully!");
          setTimeout(() => setSuccessMessage(""), 3000);
        } else {
          console.error("Failed to add shop:", response.data);
        }
      } catch (error) {
        console.error("Error saving shop:", error);
      }
    }
  };

  const resetForm = () => {
    const initialData = {};
    entityConfig.formFields.forEach((field) => {
      initialData[field.name] = "";
    });
    setFormData(initialData);
    setEditMode(false);
    setEditingEntityId(null);
  };

  const handleEdit = (entityId) => {
    const entityToEdit = entities.find((entity) => entity.shop_id === entityId);
    if (entityToEdit) {
      const updatedFormData = {};
      entityConfig.formFields.forEach((field) => {
        updatedFormData[field.name] = entityToEdit[field.name] || "";
      });
      setFormData(updatedFormData);
      setEditMode(true);
      setEditingEntityId(entityId);
      setIsTableModalOpen(false);
      if (firstFormFieldRef.current) {
        firstFormFieldRef.current.focus();
      }
    }
  };



  return (
    <div className="shops">
      <EntityManagement
        entityName={entityConfig.name}
        formFields={entityConfig.formFields}
        formData={formData}
        setFormData={setFormData}
        handleFormSubmit={handleFormSubmit}
        dropdownData={{
          merchant: merchantOptions.options || [],
          domain: domainOptions.options || [],
        }}
        setDropdownData={() => {}}
        showWorkdays={false}
        handleViewTableClick={() => setIsTableModalOpen(true)}
        firstFormFieldRef={firstFormFieldRef}
        successMessage={successMessage}
      />

      {isTableModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <button onClick={() => setIsTableModalOpen(false)}>Close</button>
            <EntityTable
              data={entities}
              columns={entityConfig.columns}
              onEdit={handleEdit}       
              keyField="shop_id"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Shops;
